import React, { useState, useEffect } from 'react';
import { Bell, BellRing, Check, X, Loader } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import {
  pushSupported,
  subscribeToPush,
  persistSavedSearch,
  getExistingSubscription,
} from '../utils/pushSubscription';

/**
 * "Save this search + get a push when a matching property arrives" button.
 *
 * Rendered from the Properties page. Only visible on browsers that support
 * Web Push (excludes iOS Safari, iOS Chrome, Firefox mobile — deliberate,
 * matches PWA install policy).
 *
 * Flow:
 *   1. Click button → open inline modal with disclosure + optional email
 *   2. User confirms → we call subscribeToPush() inside the click gesture,
 *      then POST filters + PushSubscription to backend.
 *   3. Backend fires a "welcome" push so the user immediately sees the
 *      plumbing works.
 *
 * The button is intentionally understated (matches the CasaOthers minimalist
 * aesthetic) but has enough copy that the value prop is clear.
 */
const SavedSearchButton = ({ filters, activeCount }) => {
  const { language } = useLanguage();
  const isNL = language === 'nl';
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | working | success | error
  const [errorMsg, setErrorMsg] = useState('');
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [supported, setSupported] = useState(false);

  useEffect(() => {
    // Only reveal the button if the browser is actually push-capable.
    // Doing this in an effect avoids SSR hydration mismatches.
    setSupported(pushSupported());
    (async () => {
      const existing = await getExistingSubscription();
      if (existing) setAlreadySubscribed(true);
    })();
  }, []);

  if (!supported) return null;

  const openModal = () => {
    setStatus('idle');
    setErrorMsg('');
    setOpen(true);
  };

  const closeModal = () => {
    if (status === 'working') return; // don't let user close mid-request
    setOpen(false);
  };

  const onConfirm = async () => {
    setStatus('working');
    setErrorMsg('');
    try {
      const sub = await subscribeToPush();
      await persistSavedSearch({
        email: email.trim(),
        language,
        filters,
        subscription: sub,
      });
      setStatus('success');
      setAlreadySubscribed(true);
      // Auto-close after a moment so the user sees confirmation
      setTimeout(() => setOpen(false), 2200);
    } catch (e) {
      const code = e?.message || 'unknown';
      let msg;
      if (code === 'permission-denied') {
        msg = isNL
          ? 'U heeft de meldingen geweigerd. Sta ze toe in uw browserinstellingen om deze functie te gebruiken.'
          : 'You declined notifications. Enable them in your browser settings to use this feature.';
      } else if (code === 'unsupported') {
        msg = isNL
          ? 'Uw browser ondersteunt geen pushmeldingen.'
          : 'Your browser does not support push notifications.';
      } else {
        msg = isNL
          ? 'Er ging iets mis. Probeer het later opnieuw.'
          : 'Something went wrong. Please try again later.';
      }
      setErrorMsg(msg);
      setStatus('error');
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        style={styles.trigger}
        data-testid="save-search-button"
      >
        {alreadySubscribed ? <BellRing size={16} /> : <Bell size={16} />}
        <span>
          {alreadySubscribed
            ? (isNL ? 'Meldingen actief — beheer' : 'Alerts active — manage')
            : (isNL ? 'Bewaar deze zoekopdracht' : 'Save this search')}
        </span>
        {activeCount > 0 && (
          <span style={styles.badge} data-testid="save-search-count">
            {activeCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          style={styles.backdrop}
          onClick={closeModal}
          data-testid="save-search-modal"
        >
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={closeModal}
              style={styles.close}
              aria-label={isNL ? 'Sluiten' : 'Close'}
              data-testid="save-search-close"
            >
              <X size={18} />
            </button>

            <div style={styles.badgeIcon}>
              <BellRing size={22} color="#1A1918" />
            </div>

            <h2 style={styles.title}>
              {isNL ? 'Ontvang meldingen bij matches' : 'Get notified on matches'}
            </h2>
            <p style={styles.subtitle}>
              {isNL
                ? 'We sturen u een pushmelding zodra een nieuwe woning voldoet aan uw huidige filters. U kunt zich altijd afmelden.'
                : "We'll send you a push notification the moment a new property matches your current filters. Unsubscribe anytime."}
            </p>

            <label style={styles.label}>
              {isNL ? 'E-mailadres (optioneel)' : 'Email (optional)'}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={isNL ? 'uw@email.nl' : 'you@email.com'}
              style={styles.input}
              disabled={status === 'working' || status === 'success'}
              data-testid="save-search-email"
            />
            <p style={styles.helper}>
              {isNL
                ? 'We gebruiken uw e-mailadres alleen om u persoonlijk contact op te nemen als er een uitzonderlijke match is.'
                : 'We only use your email to reach out personally when there is an exceptional match.'}
            </p>

            {status === 'error' && (
              <div style={styles.error} data-testid="save-search-error">
                {errorMsg}
              </div>
            )}
            {status === 'success' && (
              <div style={styles.success} data-testid="save-search-success">
                <Check size={16} />
                <span>
                  {isNL
                    ? 'Opgeslagen! Verwacht een testmelding binnen enkele seconden.'
                    : 'Saved! Expect a test notification in a few seconds.'}
                </span>
              </div>
            )}

            <div style={styles.actions}>
              <button
                type="button"
                onClick={closeModal}
                style={styles.secondaryBtn}
                disabled={status === 'working'}
                data-testid="save-search-cancel"
              >
                {isNL ? 'Annuleren' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={onConfirm}
                style={styles.primaryBtn}
                disabled={status === 'working' || status === 'success'}
                data-testid="save-search-confirm"
              >
                {status === 'working' && <Loader size={14} className="spin" />}
                {status === 'success'
                  ? (isNL ? 'Opgeslagen' : 'Saved')
                  : status === 'working'
                    ? (isNL ? 'Bezig...' : 'Working...')
                    : (isNL ? 'Sta meldingen toe' : 'Enable notifications')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const styles = {
  trigger: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.55rem 1rem',
    backgroundColor: 'transparent',
    border: '1px solid #C5A059',
    color: '#1A1918',
    fontFamily: "'Manrope', sans-serif",
    fontSize: '0.82rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    transition: 'background 0.15s ease',
  },
  badge: {
    backgroundColor: '#C5A059',
    color: '#1A1918',
    fontSize: '0.7rem',
    padding: '0.1rem 0.45rem',
    marginLeft: '0.15rem',
    fontWeight: 700,
  },
  backdrop: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(26, 25, 24, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9998,
    padding: '1rem',
    backdropFilter: 'blur(2px)',
  },
  modal: {
    position: 'relative',
    width: '100%',
    maxWidth: '460px',
    backgroundColor: '#FAF9F6',
    padding: '2rem 1.75rem 1.5rem',
    fontFamily: "'Manrope', sans-serif",
    color: '#1A1918',
    boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
    borderTop: '3px solid #C5A059',
  },
  close: {
    position: 'absolute', top: '0.5rem', right: '0.5rem',
    background: 'transparent', border: 'none',
    color: '#8C8B88', cursor: 'pointer',
    padding: '0.5rem',
  },
  badgeIcon: {
    width: '48px', height: '48px',
    backgroundColor: '#C5A059',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: '0.9rem',
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.4rem',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#1A1918',
    lineHeight: 1.25,
  },
  subtitle: {
    fontSize: '0.9rem',
    color: '#585755',
    lineHeight: 1.5,
    marginBottom: '1.25rem',
  },
  label: {
    display: 'block',
    fontSize: '0.72rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#8C8B88',
    marginBottom: '0.4rem',
  },
  input: {
    width: '100%',
    padding: '0.7rem 0.85rem',
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    color: '#1A1918',
    backgroundColor: 'white',
    border: '1px solid #EAE5DD',
    outline: 'none',
    boxSizing: 'border-box',
  },
  helper: {
    fontSize: '0.72rem',
    color: '#8C8B88',
    marginTop: '0.35rem',
    lineHeight: 1.45,
  },
  error: {
    marginTop: '0.9rem',
    padding: '0.65rem 0.85rem',
    fontSize: '0.82rem',
    color: '#8A2A1F',
    backgroundColor: '#F7E5E2',
    border: '1px solid #E9C0B8',
  },
  success: {
    marginTop: '0.9rem',
    padding: '0.65rem 0.85rem',
    fontSize: '0.82rem',
    color: '#1F5D2E',
    backgroundColor: '#E5F1E7',
    border: '1px solid #B8D9BF',
    display: 'flex', alignItems: 'center', gap: '0.4rem',
  },
  actions: {
    marginTop: '1.5rem',
    display: 'flex', justifyContent: 'flex-end',
    gap: '0.6rem',
  },
  secondaryBtn: {
    padding: '0.7rem 1.1rem',
    fontSize: '0.82rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 600,
    fontFamily: 'inherit',
    backgroundColor: 'transparent',
    border: '1px solid #EAE5DD',
    color: '#585755',
    cursor: 'pointer',
  },
  primaryBtn: {
    display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
    padding: '0.7rem 1.25rem',
    fontSize: '0.82rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    fontWeight: 700,
    fontFamily: 'inherit',
    backgroundColor: '#C5A059',
    color: 'white',
    border: '1px solid #C5A059',
    cursor: 'pointer',
  },
};

export default SavedSearchButton;
